import {
  MessageAppend,
  TransactionButtonProps,
  TransactionContainerProps,
} from '@bitte-ai/types';
import { Transaction as SuiTransaction } from '@mysten/sui/transactions';
import { Transaction } from '@near-wallet-selector/core';
import { ArrowRight, Copy, ExternalLink, X } from 'lucide-react';
import { useMemo, useState } from 'react';
import { TransactionRequest } from 'viem';
import { useEVMTransaction } from '../../../hooks/useEvmTransaction';
import { useTransaction } from '../../../hooks/useTransaction';
import { getChainIcon, getTokenIcon } from '../../../lib/token-mapping';
import { createDeclineTransactionMessage } from '../../../lib/transaction-messages';
import { useAccount } from '../../../state/wallet.state';
import {
  getExplorerUrl,
  transactionRequestToSignRequestData,
} from '../../../utils/evm';
import { Button } from '../../ui/button';
import { CardFooter, CardHeader } from '../../ui/card';
import DefaultTxApproveButton from '../default-components/DefaultTxApproveButton';
import DefaultTxContainer from '../default-components/DefaultTxContainer';
import DefaultTxDeclineButton from '../default-components/DefaultTxDeclineButton';
import LoadingMessage from '../loading/LoadingMessage';

interface IntentsData {
  success: boolean;
  quote?: {
    amountIn: string;
    amountInFormatted: string;
    amountOut: string;
    amountOutFormatted: string;
    depositAddress: string;
    timeEstimate: number;
  };
  fromToken?: {
    symbol: string;
    blockchain: string;
    decimals: number;
    icon?: string;
  };
  toToken?: {
    symbol: string;
    blockchain: string;
    decimals: number;
    icon?: string;
  };
  nearTx?: Transaction;
  evmTx?: TransactionRequest;
  suiTx?: string;
  depositAddress?: string;
  error?: string;
  recipientAddress?: string;
}

interface IntentsCardProps {
  data: IntentsData;
  messageBackgroundColor: string;
  borderColor: string;
  textColor: string;
  customTxContainer?: React.ComponentType<TransactionContainerProps>;
  customApproveTxButton?: React.ComponentType<TransactionButtonProps>;
  customDeclineTxButton?: React.ComponentType<TransactionButtonProps>;
  append: MessageAppend;
  messageId: string;
}

interface StatusResponse {
  status: string;
  swapDetails: {
    destinationChainTxHashes: { hash: string }[];
    originChainTxHashes: { hash: string }[];
    amountOut?: string;
    amountOutFormatted?: string;
  };
}

const EVM_CHAINS = {
  eth: 1,
  base: 8453,
  arb: 42161,
  gnosis: 100,
} as const;

export const IntentsCard = ({
  data,
  messageBackgroundColor,
  borderColor,
  textColor,
  customTxContainer: TxContainer = DefaultTxContainer,
  customApproveTxButton: ApproveButton = DefaultTxApproveButton,
  customDeclineTxButton: DeclineButton = DefaultTxDeclineButton,
  append,
  messageId,
}: IntentsCardProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isDeclined, setIsDeclined] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [swapStatus, setSwapStatus] = useState<StatusResponse | null>(null);
  const [isPolling, setIsPolling] = useState(false);

  const { wallet, account, evmAddress, evmWallet, suiWallet } = useAccount();
  const { handleTxn } = useTransaction({
    account,
    wallet,
    evmWallet,
    suiWallet,
  });

  const evmData = useMemo(() => {
    if (!data.evmTx || !data.fromToken?.blockchain || !evmAddress) {
      return undefined;
    }

    const chainId =
      EVM_CHAINS[data.fromToken.blockchain as keyof typeof EVM_CHAINS];
    if (!chainId) {
      console.warn('Unsupported blockchain:', data.fromToken.blockchain);
      return undefined;
    }

    return transactionRequestToSignRequestData(
      data.evmTx,
      chainId,
      evmAddress as `0x${string}`,
    );
  }, [data.evmTx, data.fromToken?.blockchain, evmAddress]);

  const { handleSmartAction: handleEvmAction } = useEVMTransaction({
    evmData,
    append,
    messageId,
  });

  const submitDepositHash = async (
    txHash: string,
    depositAddress: string,
  ): Promise<void> => {
    try {
      const response = await fetch(
        'https://1click.chaindefuser.com/v0/deposit/submit',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ txHash, depositAddress }),
        },
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
    } catch (error) {
      console.warn('Failed to submit deposit hash:', error);
    }
  };

  const pollSwapStatus = async (depositAddress: string) => {
    setIsPolling(true);
    const maxAttempts = 60;
    let attempts = 0;

    const poll = async () => {
      try {
        const response = await fetch(
          `https://1click.chaindefuser.com/v0/status?depositAddress=${depositAddress}`,
        );
        const status: StatusResponse = await response.json();

        setSwapStatus(status);

        if (
          status.status === 'SUCCESS' ||
          status.swapDetails.destinationChainTxHashes.length > 0
        ) {
          setIsPolling(false);
          return;
        }

        if (status.status === 'FAILED' || status.status === 'REFUNDED') {
          setIsPolling(false);
          setErrorMsg(`Swap ${status.status.toLowerCase()}`);
          return;
        }

        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, 2000);
        } else {
          setIsPolling(false);
          setErrorMsg('Swap status polling timeout');
        }
      } catch (error) {
        console.error('Error polling swap status:', error);
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, 5000);
        } else {
          setIsPolling(false);
          setErrorMsg('Failed to get swap status');
        }
      }
    };

    poll();
  };

  const handleApproveSwap = async () => {
    setIsLoading(true);
    setErrorMsg('');

    try {
      let txHash: string | undefined;

      if (data.nearTx) {
        const result = await handleTxn({ transactions: [data.nearTx] });
        txHash = result?.near?.receipts?.[0]?.transaction?.hash;
      } else if (data.evmTx && evmData) {
        await handleEvmAction();
        txHash = evmWallet?.hash;
      } else if (data.suiTx) {
        const bytes = Buffer.from(data.suiTx, 'base64');
        const suiTransaction = SuiTransaction.from(bytes);
        const result = await handleTxn({ suiTransaction });
        txHash = result?.sui?.digest;
      }

      if (data.depositAddress) {
        if (txHash) {
          await submitDepositHash(txHash, data.depositAddress);
        }
        await pollSwapStatus(data.depositAddress);
      }
    } catch (error: any) {
      setErrorMsg(error?.message);
    } finally {
      setIsLoading(false);
    }
  };

  const declineSwap = async () => {
    setIsDeclined(true);
    await append(createDeclineTransactionMessage(messageId));
  };
  if (!data.success || data.error) {
    return (
      <TxContainer
        style={{
          backgroundColor: messageBackgroundColor,
          borderColor,
          textColor,
        }}
      >
        <div className="bitte-p-4 bitte-text-center bitte-text-red-400">
          {data.error || 'Failed to get swap quote'}
        </div>
      </TxContainer>
    );
  }

  if (!data.quote || !data.fromToken || !data.toToken) {
    return (
      <TxContainer
        style={{
          backgroundColor: messageBackgroundColor,
          borderColor,
          textColor,
        }}
      >
        <div className="bitte-p-4 bitte-text-center">Invalid swap data</div>
      </TxContainer>
    );
  }

  const exchangeRate = useMemo(() => {
    if (!data.quote?.amountOutFormatted || !data.quote?.amountInFormatted) {
      return '—';
    }
    return (
      Number.parseFloat(data.quote.amountOutFormatted) /
      Number.parseFloat(data.quote.amountInFormatted)
    ).toFixed(6);
  }, [data.quote?.amountOutFormatted, data.quote?.amountInFormatted]);

  return (
    <TxContainer
      style={{
        backgroundColor: messageBackgroundColor,
        borderColor,
        textColor,
      }}
    >
      <CardHeader
        className="bitte-border-b bitte-text-center bitte-py-4"
        style={{ borderColor }}
      >
        <p className="bitte-text-xl bitte-font-bold bitte-text-white">
          NEAR Intents Swap
        </p>
      </CardHeader>

      <div className="bitte-p-6">
        <div className="bitte-flex bitte-flex-col bitte-space-y-6 bitte-w-full">
          {/* Swap Interface */}
          <div className="bitte-flex bitte-flex-col bitte-space-y-4 sm:bitte-flex-row sm:bitte-items-center sm:bitte-space-y-0 sm:bitte-space-x-4">
            {/* From Token */}
            <div className="bitte-flex-1 bitte-min-w-0">
              <div className="bitte-px-5 bitte-py-4 bitte-rounded-xl bitte-border bitte-border-gray-700/50 bitte-shadow-lg bitte-transition-all bitte-duration-200 hover:bitte-border-gray-600/50">
                <div className="bitte-flex bitte-items-center bitte-space-x-3">
                  {getTokenIcon(data.fromToken.symbol) ? (
                    <img
                      src={getTokenIcon(data.fromToken.symbol)}
                      alt={data.fromToken.symbol}
                      className="bitte-h-10 bitte-w-10 bitte-rounded-full bitte-object-cover bitte-ring-2 bitte-ring-gray-600/50"
                    />
                  ) : (
                    <div className="bitte-h-10 bitte-w-10 bitte-rounded-full bitte-bg-gradient-to-br bitte-from-blue-500 bitte-to-blue-600 bitte-flex bitte-items-center bitte-justify-center bitte-text-white bitte-font-bold bitte-text-sm bitte-shadow-lg">
                      {data.fromToken.symbol.charAt(0)}
                    </div>
                  )}
                  <div className="bitte-flex bitte-flex-col bitte-min-w-0">
                    <div className="bitte-text-white bitte-font-semibold bitte-text-lg bitte-truncate">
                      {data.quote.amountInFormatted} {data.fromToken.symbol}
                    </div>
                    <div className="bitte-text-gray-300 bitte-text-sm bitte-font-medium bitte-flex bitte-items-center bitte-space-x-1">
                      <span>From</span>
                      {getChainIcon(data.fromToken.blockchain) && (
                        <img
                          src={getChainIcon(data.fromToken.blockchain)}
                          alt={data.fromToken.blockchain}
                          className="bitte-h-4 bitte-w-4"
                        />
                      )}
                      <span>{data.fromToken.blockchain.toUpperCase()}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Arrow */}
            <div className="bitte-flex bitte-justify-center bitte-py-2">
              <div className="bitte-p-2 bitte-rounded-full bitte-shadow-lg">
                <ArrowRight className="bitte-w-5 bitte-h-5 bitte-text-white" />
              </div>
            </div>

            {/* To Token */}
            <div className="bitte-flex-1 bitte-min-w-0">
              <div className="bitte-px-5 bitte-py-4 bitte-rounded-xl bitte-border bitte-border-gray-700/50 bitte-shadow-lg bitte-transition-all bitte-duration-200 hover:bitte-border-gray-600/50">
                <div className="bitte-flex bitte-items-center bitte-space-x-3">
                  {getTokenIcon(data.toToken.symbol) ? (
                    <img
                      src={getTokenIcon(data.toToken.symbol)}
                      alt={data.toToken.symbol}
                      className="bitte-h-10 bitte-w-10 bitte-rounded-full bitte-object-cover bitte-ring-2 bitte-ring-gray-600/50"
                    />
                  ) : (
                    <div className="bitte-h-10 bitte-w-10 bitte-rounded-full bitte-bg-gradient-to-br bitte-from-green-500 bitte-to-green-600 bitte-flex bitte-items-center bitte-justify-center bitte-text-white bitte-font-bold bitte-text-sm bitte-shadow-lg">
                      {data.toToken.symbol.charAt(0)}
                    </div>
                  )}
                  <div className="bitte-flex bitte-flex-col bitte-min-w-0">
                    <div className="bitte-text-white bitte-font-semibold bitte-text-lg bitte-truncate">
                      {data.quote.amountOutFormatted} {data.toToken.symbol}
                    </div>
                    <div className="bitte-text-gray-300 bitte-text-sm bitte-font-medium bitte-flex bitte-items-center bitte-space-x-1">
                      <span>To</span>
                      {getChainIcon(data.toToken.blockchain) && (
                        <img
                          src={getChainIcon(data.toToken.blockchain)}
                          alt={data.toToken.blockchain}
                          className="bitte-h-4 bitte-w-4"
                        />
                      )}
                      <span>{data.toToken.blockchain.toUpperCase()}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Swap Details */}
          <div className="bitte-p-4">
            <div className="bitte-flex bitte-gap-6 bitte-text-sm bitte-items-center">
              <div className="bitte-flex bitte-flex-col">
                <span className="bitte-text-gray-400 bitte-font-medium">
                  Rate
                </span>
                <span className="bitte-text-white bitte-font-semibold">
                  1 {data.fromToken.symbol} ≈ {exchangeRate}{' '}
                  {data.toToken.symbol}
                </span>
              </div>
              <div className="bitte-flex bitte-flex-col">
                <span className="bitte-text-gray-400 bitte-font-medium">
                  Time
                </span>
                <span className="bitte-text-white bitte-font-semibold">
                  {data.quote.timeEstimate}s
                </span>
              </div>
              <div className="bitte-flex bitte-flex-col">
                <span className="bitte-text-gray-400 bitte-font-medium">
                  To
                </span>
                <span className="bitte-text-white bitte-font-semibold bitte-flex bitte-items-center">
                  {data?.recipientAddress
                    ? `${data.recipientAddress.slice(0, 6)}...${data.recipientAddress.slice(-4)}`
                    : ''}
                  <button
                    type="button"
                    className="bitte-ml-1 bitte-p-1 bitte-rounded hover:bitte-bg-gray-700"
                    onClick={() =>
                      navigator.clipboard.writeText(
                        data?.recipientAddress || '',
                      )
                    }
                    aria-label="Copy address"
                  >
                    <Copy className="bitte-w-4 bitte-h-4 bitte-text-gray-400" />
                  </button>
                </span>
              </div>
            </div>
          </div>

          {/* Swap Status */}
          {swapStatus && (
            <div className="bitte-bg-gray-800/30 bitte-rounded-lg bitte-border bitte-border-gray-700/50 bitte-p-4">
              <div className="bitte-flex bitte-items-center bitte-justify-between bitte-mb-3">
                <span className="bitte-text-gray-400 bitte-font-medium">
                  Swap Status
                </span>
                <div className="bitte-flex bitte-items-center bitte-space-x-2">
                  {swapStatus.status === 'SUCCESS' ? (
                    <div className="bitte-w-2 bitte-h-2 bitte-bg-green-500 bitte-rounded-full bitte-animate-pulse" />
                  ) : swapStatus.status === 'FAILED' ||
                    swapStatus.status === 'REFUNDED' ? (
                    <div className="bitte-w-2 bitte-h-2 bitte-bg-red-500 bitte-rounded-full" />
                  ) : (
                    <div className="bitte-w-2 bitte-h-2 bitte-bg-yellow-500 bitte-rounded-full bitte-animate-pulse" />
                  )}
                  <span className="bitte-text-white bitte-font-semibold bitte-capitalize">
                    {swapStatus.status.toLowerCase().replace('_', ' ')}
                  </span>
                </div>
              </div>

              {swapStatus.swapDetails.destinationChainTxHashes.length > 0 && (
                <div className="bitte-mt-3 bitte-pt-3 bitte-border-t bitte-border-gray-700/50">
                  <span className="bitte-text-gray-400 bitte-font-medium bitte-text-sm bitte-block bitte-mb-2">
                    Destination Transaction
                  </span>
                  {swapStatus.swapDetails.destinationChainTxHashes.map(
                    (hashObj, index) => (
                      <a
                        key={index}
                        href={getExplorerUrl(
                          data.toToken!.blockchain,
                          hashObj.hash,
                        )}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="bitte-inline-flex bitte-items-center bitte-space-x-2 bitte-bg-blue-500/20 bitte-hover:bg-blue-500/30 bitte-text-blue-400 bitte-hover:text-blue-300 bitte-px-3 bitte-py-2 bitte-rounded-lg bitte-text-sm bitte-font-medium bitte-transition-all bitte-duration-200"
                      >
                        <span>View Transaction</span>
                        <ExternalLink className="bitte-w-4 bitte-h-4" />
                      </a>
                    ),
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Error Message */}
      {errorMsg && !isLoading && (
        <div className="bitte-px-6 bitte-pb-6">
          <div className="bitte-bg-gradient-to-r bitte-from-red-500/10 bitte-to-orange-500/10 bitte-rounded-lg bitte-border bitte-border-red-500/20 bitte-p-4">
            <div className="bitte-flex bitte-items-start bitte-space-x-3">
              <div className="bitte-flex-shrink-0">
                <X className="bitte-w-6 bitte-h-6 bitte-text-red-400" />
              </div>
              <div className="bitte-flex-1">
                <h3 className="bitte-text-red-300 bitte-font-semibold bitte-text-sm bitte-mb-1">
                  Transaction Error
                </h3>
                <p className="bitte-text-red-200 bitte-text-sm bitte-mb-3">
                  {errorMsg}
                </p>
                <Button
                  className="bitte-bg-red-500/20 bitte-hover:bg-red-500/30 bitte-text-red-300 bitte-border-red-500/30 bitte-hover:border-red-500/40"
                  variant="outline"
                  size="sm"
                  onClick={() => setErrorMsg('')}
                >
                  Dismiss
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Loading */}
      {(isLoading || isPolling) && (
        <div className="bitte-px-6 bitte-pb-6">
          <div className="bitte-bg-gray-800/30 bitte-rounded-lg bitte-border bitte-border-gray-700/50 bitte-p-6">
            <div className="bitte-flex bitte-flex-col bitte-items-center bitte-space-y-4">
              <div className="bitte-w-12 bitte-h-12">
                <LoadingMessage color={textColor} />
              </div>
              <div className="bitte-text-center">
                <p className="bitte-text-white bitte-font-semibold bitte-text-base bitte-mb-1">
                  {isLoading
                    ? 'Processing Transaction'
                    : 'Monitoring Swap Progress'}
                </p>
                <p className="bitte-text-gray-400 bitte-text-sm">
                  {isLoading
                    ? 'Please confirm in your wallet...'
                    : 'This may take a few moments...'}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Declined Message */}
      {isDeclined && (
        <div className="bitte-px-6 bitte-pb-6">
          <div className="bitte-bg-gradient-to-r bitte-from-gray-500/10 bitte-to-gray-600/10 bitte-rounded-lg bitte-border bitte-border-gray-500/20 bitte-p-4 bitte-text-center">
            <div className="bitte-flex bitte-items-center bitte-justify-center bitte-space-x-2">
              <X className="bitte-w-5 bitte-h-5 bitte-text-gray-400" />
              <span className="bitte-text-gray-300 bitte-font-medium">
                Swap Declined
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Action Buttons */}
      {!isLoading && !isPolling && !swapStatus && !errorMsg && !isDeclined && (
        <CardFooter className="bitte-flex bitte-items-center bitte-gap-6">
          <DeclineButton onClick={declineSwap} disabled={isLoading} />
          <ApproveButton
            onClick={handleApproveSwap}
            disabled={isLoading || (!data.nearTx && !data.evmTx && !data.suiTx)}
            isLoading={isLoading}
          />
        </CardFooter>
      )}
    </TxContainer>
  );
};
